// pages/Connector/BulkWhatsappSender/BulkWhatsappSender.js
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import backendURL from '../../../config';
import Papa from 'papaparse';
import { Stepper, Step, StepLabel, Typography, Button, Box, IconButton, Input, Card, CardContent, Grid, List, ListItem, ListItemText, Divider, Badge, TextField } from '@mui/material'; // Added TextField
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SendIcon from '@mui/icons-material/Send';

function BulkWhatsappSender() {
    const [activeStep, setActiveStep] = useState(0);
    const [csvData, setCsvData] = useState([]);
    const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
    const [messageTemplate, setMessageTemplate] = useState('');
    const [templateName, setTemplateName] = useState(''); // New state for template name
    const [responseMessage, setResponseMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const fileInputRef = useRef(null);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [sendStatusMessages, setSendStatusMessages] = useState([]); // New state for send statuses

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            Papa.parse(file, {
                header: true,
                delimiter: ',',
                newline: '\r\n',
                skipEmptyLines: true,
                complete: (results) => {
                    if (results.errors.length > 0) {
                        setErrorMessage("Error parsing CSV: " + results.errors.map(e => e.message).join(", "));
                        setCsvData([]);
                        setCsvHeaders([]);
                    } else if (results.data && results.data.length > 0) {
                        setCsvData(results.data);
                        setErrorMessage('');
                        setCsvHeaders(results.meta.fields || []);
                    } else {
                        setErrorMessage("No data found in CSV file or incorrect format.");
                        setCsvData([]);
                        setCsvHeaders([]);
                    }
                },
                error: (error) => {
                    setErrorMessage("Error reading CSV file: " + error.message);
                    setCsvData([]);
                    setCsvHeaders([]);
                }
            });
        }
    };

    const handleUploadToServer = async () => {
        setResponseMessage('');
        setErrorMessage('');
        if (fileInputRef.current.files && fileInputRef.current.files[0]) {
            const formData = new FormData();
            formData.append('csv_file', fileInputRef.current.files[0]);

            try {
                const token = localStorage.getItem("access_token");
                const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' };
                const response = await axios.post(`${backendURL}/upload-csv`, formData, { headers });
                if (response.status >= 200 && response.status < 300) {
                    setResponseMessage(response.data.message);
                    fetchRecipients();
                    fileInputRef.current.value = '';
                    handleNext(); // Move to next step after successful upload
                } else {
                    if (response.data && response.data.message) {
                        setErrorMessage(response.data.message);
                    } else {
                        setErrorMessage(`Upload failed with status: ${response.status}`);
                    }
                }
            } catch (error) {
                if (error.response && error.response.data && error.response.data.message) {
                    setErrorMessage(error.response.data.message);
                    console.error("Error uploading CSV:", error.response.data.message);
                } else {
                    setErrorMessage('Failed to upload CSV file. (Network error)');
                    console.error("Error uploading CSV:", error);
                }
            }
        } else {
            setErrorMessage("Please select a CSV file to upload.");
        }
    };


    const fetchRecipients = async () => {
        try {
            const token = localStorage.getItem("access_token");
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${backendURL}/get-recipients`, { headers });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
            setCsvData(responseData.recipients);
        } catch (error) {
            setErrorMessage("Error fetching recipients.");
            console.error("Error fetching recipients:", error);
        }
    };

    const handleRecipientSelection = (event) => {
        const recipientId = parseInt(event.target.value);
        if (event.target.checked) {
            setSelectedRecipientIds([...selectedRecipientIds, recipientId]);
        } else {
            setSelectedRecipientIds(selectedRecipientIds.filter(id => id !== recipientId));
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allIds = csvData.map(recipient => recipient.id);
            setSelectedRecipientIds(allIds);
        } else {
            setSelectedRecipientIds([]);
        }
    };

    const handleTemplateChange = (e) => {
        setMessageTemplate(e.target.value);
    };

    const handleTemplateNameChange = (e) => {
        setTemplateName(e.target.value);
    };

    const handleSaveTemplate = async () => {
        setResponseMessage('');
        setErrorMessage('');
        if (!templateName) {
            setErrorMessage("Please enter a template name.");
            return;
        }
        try {
            const token = localStorage.getItem("access_token");
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.post(`${backendURL}/save-template`, { template_name: templateName, template_content: messageTemplate }, { headers }); // Use templateName
            if (response.status >= 200 && response.status < 300) {
                setResponseMessage(response.data.message);
                fetchTemplate();
            } else {
                setErrorMessage(response.data.message || `Error: ${response.status}`);
            }
        } catch (error) {
            setErrorMessage('Failed to save message template.');
            console.error("Error saving template:", error);
        }
    };

    const fetchTemplate = async () => {
        try {
            const token = localStorage.getItem("access_token");
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${backendURL}/get-template`, { headers });
            
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

            if (responseData) {
                setMessageTemplate(responseData.template_content);
                setTemplateName(responseData.template_name); // set template name when fetching
            } else {
                setMessageTemplate('');
                setTemplateName(''); // clear template name when no template
            }
        } catch (error) {
            setErrorMessage("Error fetching message template.");
            console.error("Error fetching template:", error);
        }
    };

    const handleSendMessage = async () => {
        setResponseMessage('');
        setErrorMessage('');
        setSendStatusMessages([]); // Clear previous status messages

        if (selectedRecipientIds.length === 0) {
            setErrorMessage("Please select recipients to send messages to.");
            return;
        }
        if (!messageTemplate) {
            setErrorMessage("Please enter a message template.");
            return;
        }

        try {
            const token = localStorage.getItem("access_token");
            const headers = { Authorization: `Bearer ${token}` };
            // const response = await axios.post(`${backendURL}/send-bulk-message`, { recipient_ids: selectedRecipientIds, message_template: messageTemplate }, { headers });
            // Include templateName in the request payload here:
            const response = await axios.post(`${backendURL}/send-bulk-message`, {
                recipient_ids: selectedRecipientIds,
                message_template: messageTemplate,
                template_name: templateName // Include templateName here
            }, { headers });
            if (response.status >= 200 && response.status < 300) {
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

                setResponseMessage(responseData.message + ` Success: ${responseData.success_count}, Errors: ${responseData.error_count}`);
                // Process errors_detail to show individual statuses
                const detailedStatuses = [];
                if (responseData.errors_detail && Array.isArray(responseData.errors_detail)) {
                    responseData.errors_detail.forEach(errorDetail => {
                        detailedStatuses.push({
                            name: errorDetail.recipient_name,
                            number: errorDetail.recipient_number,
                            status: 'failed'
                        });
                    });
                }
                // Determine success status for recipients without errors
                selectedRecipientIds.forEach(recipientId => {
                    const recipient = csvData.find(r => r.id === recipientId);
                    if (recipient && !responseData.errors_detail?.some(err => err.recipient_name === recipient.name)) {
                        detailedStatuses.push({
                            name: recipient.name,
                            number: recipient.whatsapp_number,
                            status: 'success'
                        });
                    }
                });
                setSendStatusMessages(detailedStatuses); // Update status messages for display
                // setSelectedRecipientIds([]); // Keep selected recipients for status display
                // handleNext(); // Do not move to next step immediately, stay on step 4 to show statuses
            } else {
                setErrorMessage(response.data.message || `Error: ${response.status}`);
            }
        } catch (error) {
            setErrorMessage('Failed to send bulk messages.');
            console.error("Error sending bulk messages:", error);
        }
    };

    const handleSkipUpload = () => {
        setActiveStep(1); // Directly go to "Select Recipients" step
    };


    useEffect(() => {
        fetchRecipients();
        fetchTemplate();
    }, []);


    const steps = ['Upload file', 'Select Recipients', 'Message Template', 'Send Bulk Messages'];
    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3">
                        <h1 className="h3 d-inline align-middle"> <strong>Bulk WhatsApp </strong> Sender</h1>
                    </div>
                    <div className="row">
                        <div className="col-12 col-lg-12">
                            <div className="card">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Upload CSV and Send Bulk WhatsApp Messages</h5>
                                </div>
                                <div className="card-body">
                                    <Box sx={{ mt: 2 }}>
                                        <Stepper activeStep={activeStep} alternativeLabel>
                                            {steps.map((label, index) => (
                                                <Step key={label} >
                                                    <StepLabel>{label}</StepLabel>
                                                </Step>
                                            ))}
                                        </Stepper>

                                        {activeStep === 0 && (
                                            <Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, border: '1px dashed grey', borderRadius: '8px', cursor: 'pointer', '&:hover': { borderColor: 'primary.main', borderStyle: 'solid' }, mb: 2 }}
                                                    onClick={() => fileInputRef.current.click()}
                                                >
                                                    <IconButton color="primary" component="span">
                                                        <CloudUploadIcon sx={{ fontSize: 60, color: 'grey' }} />
                                                    </IconButton>
                                                    <Typography variant="subtitle1" color="text.secondary">Drag and drop your import file here</Typography>
                                                    <Button variant="text" onClick={() => fileInputRef.current.click()} startIcon={<InsertDriveFileIcon />}>
                                                        Browse files
                                                    </Button>
                                                    <Typography variant="caption" color="text.secondary">File format must be .xlsx</Typography>
                                                    <input
                                                        type="file"
                                                        accept=".csv, .xlsx" //added .xlsx
                                                        onChange={handleFileUpload}
                                                        ref={fileInputRef}
                                                        style={{ display: 'none' }}
                                                    />
                                                </Box>
                                                <Button variant="outlined" onClick={handleSkipUpload}>Skip File Upload</Button>
                                            </Box>
                                        )}

                                        {activeStep === 1 && (
                                            <div>
                                                {csvData.length > 0 && (
                                                    <div>
                                                        <h4>Select Recipients</h4>
                                                        <div className="table-responsive">
                                                            <table className="table table-striped">
                                                                <thead>
                                                                    <tr>
                                                                        <th><input type="checkbox" onChange={handleSelectAll} /> Select All</th>
                                                                                        <th>Name</th>
                                                                        <th>WhatsApp Number</th>
                                                                        {csvHeaders.map((header, index) => (
                                                                            <th key={index}>{header}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {csvData.map((recipient, index) => (
                                                                        <tr key={index}>
                                                                            <td><input type="checkbox" value={recipient.id} checked={selectedRecipientIds.includes(recipient.id)} onChange={handleRecipientSelection} /></td>
                                                                            <td>{recipient.name}</td>
                                                                            <td>{recipient.whatsapp_number}</td>
                                                                            {csvHeaders.map((header, index) => (
                                                                                <td key={index}>
                                                                                    {recipient.data ? recipient.data[header] : 'N/A'}
                                                                                </td >
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                                {!csvData.length && activeStep === 1 && (
                                                    <Typography>Please upload a CSV file in the first step to select recipients.</Typography>
                                                )}
                                            </div>
                                        )}

                                        {activeStep === 2 && (
                                            <div className="mt-3">
                                                <h4>Message Template</h4>
                                                <TextField
                                                    label="Template Name"
                                                    fullWidth
                                                    value={templateName}
                                                    onChange={handleTemplateNameChange}
                                                    margin="normal"
                                                    />
                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    Use personalization variables like  etc.
                                                </Typography>
                                                <textarea
                                                    className="form-control"
                                                    rows="4"
                                                    placeholder="Enter your message template here (use {Name}, {amount}, {address} etc. for personalization)"
                                                    value={messageTemplate}
                                                    onChange={handleTemplateChange}
                                                />
                                                <Button variant="contained" className='mt-2' onClick={handleSaveTemplate}>Save Template</Button>
                                            </div>
                                        )}

                                        {activeStep === 3 && (
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h5" gutterBottom>
                                                        Confirm and Send Messages
                                                    </Typography>
                                                    <Divider sx={{ mb: 2 }} />
                                                    <List>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary={<Typography variant="subtitle1">Recipients Selected</Typography>}
                                                                secondary={<Typography variant="body1">{selectedRecipientIds.length}</Typography>}
                                                            />
                                                        </ListItem>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary={<Typography variant="subtitle1">Message Template Preview</Typography>}
                                                                secondary={<Typography variant="body1">{messageTemplate ? messageTemplate.substring(0, 100) + '...' : 'No template set'}</Typography>}
                                                            />
                                                        </ListItem>
                                                    </List>
                                                    <Divider sx={{ mt: 2, mb: 2 }} />
                                                    <Box mt={2} display="flex" justifyContent="space-between">
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={handleBack}
                                                        >
                                                            Back
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="success"
                                                            onClick={handleSendMessage}
                                                            disabled={selectedRecipientIds.length === 0 || !messageTemplate}
                                                            endIcon={<SendIcon />}
                                                        >
                                                            <Typography variant="subtitle2" sx={{ color: 'white' }}>Send Bulk Messages</Typography>
                                                        </Button>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        )}

                                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                                            <Button
                                                color="inherit"
                                                disabled={activeStep === 0}
                                                onClick={handleBack}
                                                sx={{ mr: 1 }}
                                            >
                                                Back
                                            </Button>
                                            <Box sx={{ flexGrow: 1 }} />
                                            {activeStep === 0 && (
                                                <Button variant="contained" onClick={handleUploadToServer} disabled={!fileInputRef.current?.files?.length}>
                                                    Upload CSV
                                                </Button>
                                            )}
                                            {activeStep === 1 && (
                                                <Button variant="contained" onClick={handleNext} disabled={csvData.length === 0}>
                                                    Next
                                                </Button>
                                            )}
                                            {activeStep === 2 && (
                                                <Button variant="contained" onClick={handleNext} disabled={!messageTemplate}>
                                                    Next
                                                </Button>
                                            )}
                                        </Box>

                                        {activeStep === 3 && sendStatusMessages.length > 0 && ( // Conditionally render status messages
                                            <Box mt={3}>
                                                <Typography variant="h6" gutterBottom>Sending Status:</Typography>
                                                <Grid container spacing={2}> {/* Use Grid for layout */}
                                                    {sendStatusMessages.map((status, index) => (
                                                        <Grid item xs={12} sm={6} md={4} key={index}> {/* Responsive Grid items */}
                                                            <Card variant="outlined">
                                                                <CardContent style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> {/* Flex row layout */}
                                                                    <Box> {/* Box for Name and Number */}
                                                                        <Typography variant="subtitle2" gutterBottom>{status.name}</Typography>
                                                                        <Typography variant="body2" color="textSecondary">{status.number}</Typography>
                                                                    </Box>
                                                                    <Box> {/* Box for Badge - aligns to the right */}
                                                                        <Badge
                                                                            badgeContent={status.status.toUpperCase()}
                                                                            color={status.status === 'success' ? 'success' : 'error'}
                                                                        />
                                                                    </Box>
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>
                                        )}

                                        <br />
                                        {responseMessage && <div className="alert alert-info">{responseMessage}</div>}
                                        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                                    </Box>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default BulkWhatsappSender;